/**
 * @file use-placement-form.ts
 * @description Состояние шторки массового размещения: ячейки склада, режим,
 * отправка документа (phase19.24 этап 1, шаг 4).
 *
 * Вынесено из компонента по той же причине, что и сбор payload: шторка —
 * форма с сетью (справочник ячеек, проведение документа, разбор 422/409), и
 * держать это вместе с разметкой значит получить файл на 300 строк.
 *
 * Справочник ячеек грузим один раз на открытие: выбор идёт поиском по коду
 * (`BinSelectField`), а скан кладовщика превращается в `binId` тем же списком
 * — второй запрос «найди ячейку по коду» складу не нужен.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  buildPlacementPayload,
  type PlacementBuildResult,
  type PlacementMode,
} from "./stock-placement-payload";
import { parsePlacementError, type PlacementError } from "./stock-placement-error";
import { PLACEMENT_TEXT, placementSkippedNotice } from "./stock-placement-texts";
import { binsApi, type StorageBinDto } from "../../../lib/api-bins";
import { transfersApi, type PlacementResult } from "../../../lib/api-transfers";
import type { StockLevelDto } from "../../../lib/api-wms";

interface Options {
  open: boolean;
  rows: StockLevelDto[];
  /**
   * Ответ сервера: документ (или его отсутствие) и пропущенные партии.
   * Вызывающий обновляет список, показывает тост и решает, закрывать ли
   * шторку — при пропущенных партиях она остаётся открытой с полосой.
   */
  onPlaced: (result: PlacementResult, placedCount: number) => void;
  /**
   * Проведение упало. Атомарности у него нет: часть партий могла уехать, а
   * документ остаться черновиком — отбор обязан перечитаться, иначе на экране
   * висят адреса, которых уже нет (находка ревью 2026-09-20).
   */
  onFailed: () => void;
}

interface PlacementFormApi {
  bins: StorageBinDto[];
  binsLoading: boolean;
  binsError: string | null;
  mode: PlacementMode;
  setMode: (mode: PlacementMode) => void;
  sharedBinId: string;
  setSharedBinId: (binId: string) => void;
  binByRow: Record<string, string>;
  setRowBin: (levelId: string, binId: string) => void;
  /** Склад размещения — резолвер скана ячейки (phase19.22). */
  warehouseId: string;
  build: PlacementBuildResult;
  saving: boolean;
  error: PlacementError | null;
  /** Полоса «уже лежат в этой ячейке»; `null` — пропущенных не было. */
  skippedNotice: string | null;
  submit: () => Promise<void>;
}

export function usePlacementForm({
  open,
  rows,
  onPlaced,
  onFailed,
}: Options): PlacementFormApi {
  const [bins, setBins] = useState<StorageBinDto[]>([]);
  const [binsLoading, setBinsLoading] = useState(false);
  const [binsError, setBinsError] = useState<string | null>(null);
  const [mode, setMode] = useState<PlacementMode>("shared");
  const [sharedBinId, setSharedBinId] = useState("");
  const [binByRow, setBinByRow] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<PlacementError | null>(null);
  const [skippedNotice, setSkippedNotice] = useState<string | null>(null);

  // Склад отбора: строки с разных складов отсечёт `buildPlacementPayload`,
  // но справочник ячеек уже нужен — берём склад первой строки.
  const warehouseId = rows[0]?.warehouseId ?? "";

  useEffect(() => {
    if (!open || !warehouseId) return;
    let cancelled = false;
    setBinsLoading(true);
    binsApi
      .list({ warehouseId, withSummary: false })
      .then((data) => {
        if (cancelled) return;
        setBins(data);
        setBinsError(null);
      })
      .catch(() => {
        if (!cancelled) setBinsError(PLACEMENT_TEXT.binsLoadError);
      })
      .finally(() => {
        if (!cancelled) setBinsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, warehouseId]);

  // Закрыли шторку — форма чистая: следующий отбор не должен унаследовать
  // чужую ячейку и красные строки прошлой попытки.
  useEffect(() => {
    if (open) return;
    setMode("shared");
    setSharedBinId("");
    setBinByRow({});
    setError(null);
    setSkippedNotice(null);
  }, [open]);

  const setRowBin = useCallback((levelId: string, binId: string) => {
    setBinByRow((prev) => ({ ...prev, [levelId]: binId }));
  }, []);

  const build = useMemo(
    () => buildPlacementPayload({ mode, sharedBinId, binByRow, rows }),
    [mode, sharedBinId, binByRow, rows],
  );

  const submit = useCallback(async () => {
    if (!build.payload) return;
    setSaving(true);
    setError(null);
    setSkippedNotice(null);
    try {
      const result = await transfersApi.placement(build.payload);
      // Пропущенные партии — не ошибка: строки проведены, а об остальных
      // кладовщику сообщает полоса. Считаем ровно то, что реально уехало.
      const placedCount = result.transfer
        ? build.payload.items.length - result.skipped.length
        : 0;
      setSkippedNotice(
        placementSkippedNotice(result.skipped, Boolean(result.transfer)),
      );
      onPlaced(result, placedCount);
    } catch (err) {
      setError(parsePlacementError(err));
      // Даже упавшее проведение могло сдвинуть склад: перечитываем отбор, а не
      // оставляем на экране прежние адреса партий.
      onFailed();
    } finally {
      setSaving(false);
    }
  }, [build.payload, onPlaced, onFailed]);

  return {
    bins,
    binsLoading,
    binsError,
    mode,
    setMode,
    sharedBinId,
    setSharedBinId,
    binByRow,
    setRowBin,
    /** Склад размещения — резолвер скана ячейки (phase19.22). */
    warehouseId,
    build,
    saving,
    error,
    skippedNotice,
    submit,
  };
}
